import { BUTTER, CORAL, LILAC, MINT, PAPER, PEACH, SKY, lerpColor } from '../../color';
import { sag } from '../../ink';
import type { WorldDress, WorldFx } from '../../world';

const BULBS = [BUTTER, PEACH, MINT, SKY, LILAC, CORAL];
const JINGLES = 5;
const TARGET_BULBS = 12;
const WIRE_SAG = 0.06;
const STREAMERS = 4;

export const dress: WorldDress = {
  incomingCircle(k, body, color, alpha) {
    const { g, pal } = k;
    const r = body.radius;
    g.ellipse(body.x, body.y, r * 0.52 * body.scaleX, r * 0.5 * body.scaleY).fill({ color: lerpColor(color, PAPER, 0.6), alpha: 0.5 * alpha });
    for (const s of [0.36, 0.72]) g.ellipse(body.x, body.y, r * s * body.scaleX, r * body.scaleY * 0.98);
    g.stroke({ width: Math.max(1, r * 0.035), color: lerpColor(color, pal.ink, 0.45), alpha: 0.4 * alpha });
    k.highlight(body, 0.6 * alpha);
  },

  mineCircle(k, body, alpha, width, fill) {
    const { g, pal } = k;
    const r = body.radius;
    k.trace(body);
    g.fill({ color: lerpColor(PAPER, BUTTER, 0.22), alpha });
    g.ellipse(body.x, body.y, r * 0.88 * body.scaleX, r * 0.88 * body.scaleY);
    g.stroke({ width: r * 0.2, color: fill, alpha });
    k.ringDots(body, r * 0.88, JINGLES, 0.3, (x, y, _i, a) => {
      const dx = Math.cos(a + Math.PI / 2) * r * 0.08;
      const dy = Math.sin(a + Math.PI / 2) * r * 0.08;
      g.circle(x + dx, (y - body.y) * body.scaleY + body.y + dy, r * 0.07);
      g.circle(x - dx, (y - body.y) * body.scaleY + body.y - dy, r * 0.07);
    });
    g.fill({ color: lerpColor(BUTTER, PAPER, 0.35), alpha });
    g.stroke({ width: Math.max(1, r * 0.02), color: pal.ink, alpha: 0.6 * alpha });
    k.highlight(body, 0.5 * alpha);
    k.trace(body);
    g.stroke({ width, color: k.outline, alpha, join: 'round' });
  },

  target(k, body, alpha, _spread, reach, spin, edge) {
    const { g, pal } = k;
    k.trace(body);
    g.fill({ color: pal.spot, alpha: 0.1 * alpha });
    k.ringDots(body, reach, TARGET_BULBS, spin, (x, y, i) => {
      const tone = BULBS[i % BULBS.length] ?? BUTTER;
      g.circle(x, y, Math.max(3, k.unit * 0.022)).fill({ color: tone, alpha: 0.16 * alpha });
      g.circle(x, y, Math.max(1.6, k.unit * 0.01)).fill({ color: lerpColor(tone, PAPER, 0.35), alpha: 0.9 * alpha });
    });
    k.trace(body);
    g.stroke({ width: edge, color: pal.rim, alpha: 0.8 * alpha, join: 'round' });
  },

  pip(k, x, y, radius, on, last, alpha, width) {
    const { g, pal } = k;
    const tone = last ? MINT : BUTTER;
    if (on) g.circle(x, y, radius * 2).fill({ color: tone, alpha: 0.2 * alpha });
    g.circle(x, y, radius).fill({ color: on ? lerpColor(tone, PAPER, 0.3) : lerpColor(pal.bg, PAPER, 0.18), alpha });
    g.stroke({ width: width * 0.8, color: pal.rim, alpha: (on ? 0.9 : 0.45) * alpha });
  },

  path(k, points, count, alpha) {
    const { g, pal } = k;
    g.moveTo(points[0]!, points[1]!);
    for (let i = 2; i < count; i += 2) {
      const x0 = points[i - 2]!;
      const y0 = points[i - 1]!;
      const x1 = points[i]!;
      const y1 = points[i + 1]!;
      const mid = sag(x0, y0, x1, y1, WIRE_SAG);
      g.quadraticCurveTo(2 * mid.x - (x0 + x1) / 2, 2 * mid.y - (y0 + y1) / 2, x1, y1);
    }
    g.stroke({ width: 1.3, color: pal.path, alpha: alpha * 2.2, cap: 'round', join: 'round' });
    for (let i = 2; i < count; i += 2) {
      const mid = sag(points[i - 2]!, points[i - 1]!, points[i]!, points[i + 1]!, WIRE_SAG);
      g.circle(mid.x, mid.y + 3, Math.max(2, k.unit * 0.009)).fill({ color: BULBS[(i / 2) % BULBS.length] ?? BUTTER, alpha: alpha * 3.5 });
    }
  },
};

export const fx: WorldFx = {
  bit(g, x, y, size, i, color, alpha, rot) {
    if (i % 4 === 0) g.star(x, y, 5, size * 1.2, size * 0.5, rot);
    else g.regularPoly(x, y, size, 4, rot);
    g.fill({ color, alpha });
  },

  milestone(g, m, pal) {
    for (let s = 0; s < STREAMERS; s++) {
      const a = (s / STREAMERS) * Math.PI * 2 + 0.4;
      g.moveTo(m.x + Math.cos(a) * m.r * 0.6, m.y + Math.sin(a) * m.r * 0.6);
      for (let k = 1; k <= 10; k++) {
        const t = k / 10;
        const wave = Math.sin(t * Math.PI * 3 + s) * m.unit * 0.03;
        const d = m.r * (0.6 + 0.9 * t);
        g.lineTo(m.x + Math.cos(a) * d - Math.sin(a) * wave, m.y + Math.sin(a) * d + Math.cos(a) * wave + m.grow * m.unit * 0.08 * t);
      }
      g.stroke({ width: m.width, color: pal.sparks[s % pal.sparks.length] ?? BUTTER, alpha: m.fade * 0.8, cap: 'round', join: 'round' });
    }
  },

  trail(g, x, y, size, angle) {
    g.star(x, y, 4, size * 1.2, size * 0.3, angle);
  },
};
